import type { Document } from '@contentful/rich-text-types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { Icon, IconName } from '@packages/shared-ui';
import RichText from '@packages/shared-ui/v4/rich-text';
import { useId } from 'react';
import useStyles from './AgreementAccordion.styles';

interface Props {
  title: string;
  terms: Document;
  isExpanded: boolean;
  isAgreed: boolean;
  onToggle: () => void;
  onAgreedChange: (isAgreed: boolean) => void;
}

export default function AgreementAccordion({
  title,
  terms,
  isExpanded,
  isAgreed,
  onToggle,
  onAgreedChange,
}: Props) {
  const { classes } = useStyles();
  const id = useId();

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onToggle}
      disableGutters
      square
      className={classes.root}
      slotProps={{ heading: { component: 'h2' } }}
    >
      <AccordionSummary
        id={`${id}-header`}
        aria-controls={`${id}-content`}
        className={classes.summary}
        expandIcon={<Icon name={IconName.CHEVRON_DOWN} width={24} />}
      >
        {isAgreed && (
          <Icon
            name={IconName.CHECK_CIRCLE_OUTLINE}
            width={32}
            classname={classes.agreedIcon}
            aria-label='Agreed'
            role='img'
          />
        )}
        <Typography variant='h3' component='span'>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails id={`${id}-content`} className={classes.details}>
        <RichText
          content={terms}
          className={classes.terms}
          variants={{ p: 'bodySm' }}
        />
        <FormControlLabel
          className={classes.consent}
          control={
            <Checkbox
              checked={isAgreed}
              onChange={(_event, checked) => onAgreedChange(checked)}
              disableRipple
              className={classes.checkbox}
              icon={<Icon name={IconName.CHECKBOX_UNCHECKED} width={24} />}
              checkedIcon={<Icon name={IconName.CHECKBOX_CHECKED} width={24} />}
            />
          }
          label={
            <Typography variant='bodySm'>
              {`I agree to enroll in ${title} at the selected plan price per billing cycle (monthly or annual), which auto-renews until cancelled, with automatic charges beginning today.`}
            </Typography>
          }
        />
      </AccordionDetails>
    </Accordion>
  );
}
